import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { FileSignature, Plus, Send, ExternalLink, CheckCircle2, Clock, X, Trash2, Copy, GitBranch, TrendingUp } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { formatMoney, formatDate } from "@/lib/format";

const STATUS_PILL = {
  draft:     "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  sent:      "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  viewed:    "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  signed:    "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  declined:  "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  expired:   "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  converted: "bg-[#C9A356]/15 text-[#8A6B14] dark:text-[#DDBD77]",
};

export default function AdminEstimates() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/estimates").then((r) => setItems(r.data)).catch((err) => {
    console.warn("Estimates list fetch failed:", err);
  });
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-estimates">
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <p className="overline">Sales pipeline</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Estimates</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">Send signed quotes that auto-convert to invoices.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-accent" data-testid="new-estimate-btn">
          <Plus className="w-4 h-4 mr-1" /> New estimate
        </button>
      </div>

      {showForm && <NewEstimateForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}

      <div className="card-pb overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400">
            <tr>
              <th className="px-4 py-3 text-left">Estimate</th>
              <th className="px-4 py-3 text-left">Client</th>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Created</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.estimate_id} className="border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`row-est-${e.estimate_id}`}>
                <td className="px-4 py-2.5 font-mono text-xs">
                  <Link to={`/admin/estimates/${e.estimate_id}`} className="text-[#0A2540] dark:text-white hover:text-[#C9A356]">{e.estimate_id}</Link>
                </td>
                <td className="px-4 py-2.5">
                  <div className="text-[#0A2540] dark:text-white">{e.client_name || "—"}</div>
                  <div className="text-[10px] font-mono text-[#94A3B8]">{e.customer_code}</div>
                </td>
                <td className="px-4 py-2.5 truncate max-w-xs">{e.title}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-medium">{formatMoney(e.grand_total)}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${STATUS_PILL[e.status] || STATUS_PILL.draft}`}>{e.status}</span>
                </td>
                <td className="px-4 py-2.5 text-xs text-[#94A3B8]">{formatDate(e.created_at)}</td>
                <td className="px-4 py-2.5">
                  <Link to={`/admin/estimates/${e.estimate_id}`} className="text-[#475569] hover:text-[#C9A356]" data-testid={`open-est-${e.estimate_id}`}>
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-16">
                <EmptyState
                  icon={FileSignature}
                  title="No estimates yet"
                  body="Send your first signed quote — it becomes a legally-locked snapshot and auto-converts to an invoice."
                  action={{ label: "New estimate", onClick: () => setShowForm(true), testId: "empty-new-est-btn" }}
                />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewEstimateForm({ onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [userId, setUserId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState("");
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10);
  });
  const [taxRate, setTaxRate] = useState(0.05); // TOT default (Phase 2)
  const [discount, setDiscount] = useState(0);
  const [discountPct, setDiscountPct] = useState(0);
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: 0, property_id: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!userId) { setProperties([]); return; }
    api.get(`/properties?user_id=${userId}`).then((r) => setProperties(r.data || [])).catch(() => setProperties([]));
  }, [userId]);

  const resolveUnit = (it) => {
    // Client-side preview only — server is source of truth.
    return parseFloat(it.unit_price) || 0;
  };
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * resolveUnit(it), 0);
  const discountAmt = discountPct > 0 ? subtotal * (parseFloat(discountPct) / 100) : (parseFloat(discount) || 0);
  const taxable = Math.max(0, subtotal - discountAmt);
  const taxAmt = taxable * (parseFloat(taxRate) || 0);
  const grand = taxable + taxAmt;
  const needsApproval = parseFloat(discountPct) >= 10;

  const submit = async () => {
    if (!userId) { toast.error("Pick a client"); return; }
    if (!items.length || !items[0].description) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      await api.post("/estimates", {
        user_id: userId,
        title: title || "Estimate",
        line_items: items.map((it) => {
          const item = { description: it.description, qty: parseFloat(it.qty) || 0, unit_price: parseFloat(it.unit_price) || 0 };
          if (it.property_id) item.property_id = it.property_id;
          return item;
        }),
        tax_rate: parseFloat(taxRate) || 0,
        discount: parseFloat(discount) || 0,
        discount_pct: parseFloat(discountPct) || 0,
        property_id: propertyId || null,
        valid_until: validUntil,
        scope_notes: scope,
      });
      toast.success(needsApproval
        ? "Estimate created — awaiting discount approval before send"
        : "Estimate created — open it to send the sign link");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-2xl w-full overflow-hidden my-8" onClick={(e) => e.stopPropagation()} data-testid="new-est-form">
        <div className="px-6 py-4 border-b border-[#E2E8F0] dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-heading text-xl text-[#0A2540] dark:text-white">New estimate</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client *">
              <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="est-client"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm">
                <option value="">— pick a client —</option>
                {clients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name} · {c.customer_code}</option>)}
              </select>
            </Field>
            <Field label="Valid until">
              <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="est-valid-until"
                     className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 5kW Solar + 10kWh Battery" data-testid="est-title"
                   className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
          </Field>

          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-[#475569] dark:text-slate-400 mb-2">Line items</div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="space-y-1">
                  <div className="grid grid-cols-[1fr_60px_100px_30px] gap-2 items-center">
                    <input value={it.description} placeholder="Description" data-testid={`est-li-desc-${i}`}
                           onChange={(e) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                           className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1.5 text-sm" />
                    <input type="number" step="0.01" value={it.qty} placeholder="Qty" data-testid={`est-li-qty-${i}`}
                           onChange={(e) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))}
                           className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1.5 text-sm text-right" />
                    <input type="number" step="0.01" value={it.unit_price} placeholder="$" data-testid={`est-li-price-${i}`}
                           onChange={(e) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, unit_price: e.target.value } : x))}
                           className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1.5 text-sm text-right" />
                    <button onClick={() => setItems((arr) => arr.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-600" data-testid={`est-li-del-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {properties.length > 1 && (
                    <select
                      value={it.property_id || ""}
                      onChange={(e) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, property_id: e.target.value } : x))}
                      data-testid={`est-li-prop-${i}`}
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1 text-[11px]"
                    >
                      <option value="">— no property (whole quote) —</option>
                      {properties.map((p) => (
                        <option key={p.property_id} value={p.property_id}>{p.label} · {p.code}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => setItems((arr) => [...arr, { description: "", qty: 1, unit_price: 0, property_id: "" }])}
                    className="mt-2 text-xs text-[#C9A356] hover:text-[#DDBD77] inline-flex items-center gap-1" data-testid="est-add-line">
              <Plus className="w-3 h-3" /> Add line
            </button>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <Field label="Discount %">
              <input type="number" step="0.5" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} data-testid="est-discount-pct"
                     className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm text-right" />
            </Field>
            <Field label="Discount $ (flat)">
              <input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="est-discount"
                     className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm text-right"
                     disabled={parseFloat(discountPct) > 0} />
            </Field>
            <Field label="Tax rate">
              <select value={taxRate} onChange={(e) => setTaxRate(parseFloat(e.target.value))} data-testid="est-tax"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm">
                <option value={0}>0%</option>
                <option value={0.05}>5% (TOT)</option>
                <option value={0.10}>10%</option>
              </select>
            </Field>
            <div className="text-right pt-5">
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Total</div>
              <div className="font-heading text-2xl font-bold text-[#0A2540] dark:text-white tabular-nums">${grand.toFixed(2)}</div>
            </div>
          </div>

          {needsApproval && (
            <div className="text-xs bg-amber-50 dark:bg-amber-900/10 border border-amber-300 text-amber-800 dark:text-amber-300 rounded p-2" data-testid="approval-warning">
              ⚠ Discount ≥ 10% — requires manager approval before this quote can be sent.
            </div>
          )}

          <Field label="Scope notes">
            <textarea value={scope} onChange={(e) => setScope(e.target.value)} rows={3} maxLength={4000} data-testid="est-scope"
                      placeholder="Inclusions, exclusions, timeline assumptions, payment terms…"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
            <button onClick={submit} disabled={saving} className="btn-accent text-sm" data-testid="est-create-submit">
              {saving ? "Saving…" : "Create draft"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#475569] dark:text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}

// Detail page export
export function AdminEstimateDetail({ estimateId }) {
  const [est, setEst] = useState(null);
  const [signUrl, setSignUrl] = useState("");

  const load = () => api.get(`/estimates/${estimateId}`).then((r) => {
    setEst(r.data);
    if (r.data.public_token) {
      setSignUrl(`${window.location.origin}/sign/estimate/${r.data.public_token}`);
    } else {
      setSignUrl("");
    }
  }).catch((err) => {
    console.warn("Estimate detail fetch failed:", err);
  });
  useEffect(() => { if (estimateId) load(); }, [estimateId]); // eslint-disable-line

  if (!est) return <div className="text-[#475569] dark:text-slate-400">Loading estimate…</div>;

  const send = async () => {
    try {
      const r = await api.post(`/estimates/${est.estimate_id}/send`);
      setSignUrl(`${window.location.origin}${r.data.sign_url}`);
      toast.success("Estimate sent — copy the link to email/WhatsApp the client");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const approveDiscount = async (decision) => {
    const note = decision === "deny" ? prompt("Reason (optional):") || "" : "";
    try {
      await api.post(`/estimates/${est.estimate_id}/approve-discount`, { decision, note });
      toast.success(decision === "approve" ? "Discount approved" : "Discount denied");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const clone = async () => {
    if (!confirm("Clone this quote as a new draft?")) return;
    try {
      const r = await api.post(`/estimates/${est.estimate_id}/clone`);
      toast.success(`Cloned as ${r.data.estimate_id}`);
      window.location.href = `/admin/estimates/${r.data.estimate_id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Clone failed"); }
  };

  const movePipeline = async (stage) => {
    try {
      await api.patch(`/estimates/${est.estimate_id}/pipeline`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const convert = async () => {
    if (!confirm("Convert this estimate to an invoice?")) return;
    const r = await api.post(`/estimates/${est.estimate_id}/convert`);
    toast.success(`Invoice ${r.data.invoice_id} created`);
    load();
  };

  const copySignUrl = () => {
    navigator.clipboard.writeText(signUrl);
    toast.success("Sign link copied");
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-est-detail">
      <Link to="/admin/estimates" className="text-sm text-[#475569] dark:text-slate-400 hover:text-[#C9A356] inline-flex items-center gap-1 mb-4">← Back to estimates</Link>

      <div className="card-pb p-6 mb-5">
        <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
          <div>
            <p className="overline">{est.estimate_id}</p>
            <h1 className="font-heading text-2xl text-[#0A2540] dark:text-white mt-1">{est.title}</h1>
            <div className="text-sm text-[#475569] dark:text-slate-400 mt-1">{est.client_name} · <span className="font-mono">{est.customer_code}</span></div>
          </div>
          <span className={`text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full ${STATUS_PILL[est.status] || STATUS_PILL.draft}`} data-testid="est-status-pill">{est.status}</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {!["converted", "expired", "declined"].includes(est.status) && (
            <button onClick={send} className="btn-accent text-sm" data-testid="est-send-btn"
                    disabled={est.discount_approval_status === "pending"}>
              <Send className="w-4 h-4 mr-1" /> {est.status === "draft" ? "Send to client" : "Re-send link"}
            </button>
          )}
          {est.discount_approval_status === "pending" && (
            <>
              <button onClick={() => approveDiscount("approve")} className="btn-primary text-sm bg-emerald-600 hover:bg-emerald-700" data-testid="approve-discount-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve {est.discount_pct}% discount
              </button>
              <button onClick={() => approveDiscount("deny")} className="btn-outline-navy text-sm text-red-600" data-testid="deny-discount-btn">
                Deny discount
              </button>
            </>
          )}
          {est.status === "signed" && (
            <button onClick={convert} className="btn-primary text-sm" data-testid="est-convert-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Convert to invoice
            </button>
          )}
          {est.invoice_id && (
            <Link to={`/admin/invoices/${est.invoice_id}`} className="btn-outline-navy text-sm" data-testid="est-open-inv">
              <FileSignature className="w-4 h-4 mr-1" /> Open invoice {est.invoice_id}
            </Link>
          )}
          <button onClick={clone} className="btn-outline-navy text-sm" data-testid="est-clone-btn">
            <GitBranch className="w-4 h-4 mr-1" /> Clone
          </button>
          <div className="inline-flex items-center gap-1 text-xs ml-auto" data-testid="pipeline-picker">
            <TrendingUp className="w-3.5 h-3.5 text-[#C9A356]" />
            <span className="text-[#475569] dark:text-slate-400 uppercase tracking-wider text-[10px] font-bold">Pipeline</span>
            <select value={est.pipeline_stage || "proposal"} onChange={(e) => movePipeline(e.target.value)}
                    className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded px-2 py-1 text-xs">
              {["lead","qualified","proposal","negotiation","won","lost"].map((s) =>
                <option key={s} value={s}>{s}</option>
              )}
            </select>
          </div>
        </div>

        {est.snapshot_locked_at && (
          <div className="mb-4 bg-slate-50 dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 rounded p-3 text-xs text-[#475569] dark:text-slate-400" data-testid="snapshot-banner">
            🔒 <b>Snapshot-locked</b> on {new Date(est.snapshot_locked_at).toLocaleString()}. Line items & totals are frozen — clone the quote to make changes.
          </div>
        )}

        {est.discount_approval_status === "pending" && (
          <div className="mb-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-300 rounded p-3 text-sm text-amber-800 dark:text-amber-300" data-testid="pending-approval-banner">
            ⚠ This quote applies a <b>{est.discount_pct}%</b> discount. It cannot be sent until a manager with <code>quote.approve_discount</code> approves it.
          </div>
        )}
        {est.discount_approval_status === "approved" && est.discount_pct > 0 && (
          <div className="mb-4 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-300 rounded p-3 text-sm text-emerald-800 dark:text-emerald-300">
            ✓ {est.discount_pct}% discount approved{est.discount_approval_note ? ` — "${est.discount_approval_note}"` : ""}
          </div>
        )}
        {est.discount_approval_status === "denied" && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/10 border border-red-300 rounded p-3 text-sm text-red-800 dark:text-red-300">
            ✗ Discount denied{est.discount_approval_note ? ` — "${est.discount_approval_note}"` : ""}. Update the discount to send this quote.
          </div>
        )}

        {signUrl && (
          <div className="bg-[#FFFBF0] dark:bg-amber-900/10 border border-[#C9A356]/40 rounded-md p-3 text-sm flex items-center justify-between gap-3" data-testid="sign-url-card">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#C9A356] mb-1">Public sign link · valid 14 days</div>
              <div className="font-mono text-xs truncate text-[#0A2540] dark:text-white">{signUrl}</div>
            </div>
            <button onClick={copySignUrl} className="btn-accent text-xs py-1.5 px-2" data-testid="copy-sign-link">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </button>
          </div>
        )}

        {est.signed_name && (
          <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/40 rounded-md text-sm">
            <div className="font-medium text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Signed by {est.signed_name}
            </div>
            <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5">
              {new Date(est.signed_at).toLocaleString()} · IP {est.signed_ip || "—"} · {est.signed_via === "portal" ? "via portal" : "via email link"}
            </div>
            {est.signature && <img src={est.signature} alt="signature" className="mt-2 max-h-24 bg-white rounded" />}
          </div>
        )}

        {est.declined_at && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/40 rounded-md text-sm">
            <div className="font-medium text-red-700 dark:text-red-400 inline-flex items-center gap-1.5">
              <X className="w-4 h-4" /> Declined
            </div>
            <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5">{new Date(est.declined_at).toLocaleString()}</div>
            {est.decline_reason && <p className="text-sm text-[#0A2540] dark:text-white mt-1">{est.decline_reason}</p>}
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="card-pb p-6">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 border-b border-[#E2E8F0] dark:border-slate-700">
            <tr>
              <th className="py-2 text-left">Description</th>
              <th className="py-2 text-right">Qty</th>
              <th className="py-2 text-right">Unit</th>
              <th className="py-2 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {(est.line_items || []).map((li, i) => (
              <tr key={`${li.description}-${i}`} className="border-b border-[#E2E8F0] dark:border-slate-700">
                <td className="py-2">{li.description}</td>
                <td className="py-2 text-right tabular-nums">{li.qty}</td>
                <td className="py-2 text-right tabular-nums">${parseFloat(li.unit_price).toFixed(2)}</td>
                <td className="py-2 text-right tabular-nums">${(parseFloat(li.qty) * parseFloat(li.unit_price)).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 ml-auto max-w-xs space-y-1 text-sm">
          <Row label="Subtotal" value={`$${(est.subtotal || 0).toFixed(2)}`} />
          {est.discount > 0 && <Row label="Discount" value={`-$${(est.discount || 0).toFixed(2)}`} />}
          <Row label={`Tax (${(est.tax_rate * 100).toFixed(0)}%)`} value={`$${(est.tax_amount || 0).toFixed(2)}`} />
          <div className="pt-2 mt-2 border-t border-[#E2E8F0] dark:border-slate-700 flex justify-between font-bold text-[#0A2540] dark:text-white text-base">
            <span>Total</span>
            <span className="tabular-nums">${(est.grand_total || 0).toFixed(2)}</span>
          </div>
        </div>
        {est.scope_notes && (
          <div className="mt-5 pt-4 border-t border-[#E2E8F0] dark:border-slate-700">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#475569] dark:text-slate-400 mb-1">Scope</div>
            <p className="text-sm text-[#0A2540] dark:text-white whitespace-pre-wrap">{est.scope_notes}</p>
          </div>
        )}
        {est.valid_until && (
          <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#475569] dark:text-slate-400">
            <Clock className="w-3.5 h-3.5" /> Valid until {new Date(est.valid_until).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[#475569] dark:text-slate-400">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
